import type { FC, ReactNode } from "react";
import { useState } from "react";
import { Translate, useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { trackEvent } from "../../../shared/Snowplow";
import {
  myTenantsHierarchyWithCreateRegistrationPermissionQueryOptions,
  prefetchRegistrationById,
  prefetchRegistrationHistory,
  useGetRegistrationById,
} from "../../../api/registrations";
import { prefetch } from "../../../api/prefetch";
import { useDeleteRegistration } from "../../../api/registrations";
import type { RegistrationById } from "../../../api/registrations.types";
import { RegistrationControllerIcon2, RegistrationProcessorIcon2, FilesIcon, EditIcon } from "../../../shared/icons";
import { usePermission } from "../../../shared/permissions";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import type { DrawerHeaderProps } from "../../../shared/Drawer";
import { Drawer } from "../../../shared/Drawer";
import { useRegistrationVariant } from "../RegistrationVariantProvider";
import { logUnhandled } from "../../../api/logError";
import { RegistrationEditDialog } from "./RegistrationEditDialog";
import { CopyRegistrationDialog } from "./CopyRegistrationDialog";
import { useHasPermissionToUpdateMetaData } from "./useHasPermissionToUpdate";
import { ActionMenu, ActionMenuDeleteMenuItem, ActionMenuItemWithPermission } from "../../../shared/ActionMenu";

type RegistrationDrawerHeaderProps = {
  registration: RegistrationById;
  activeTab: "standard" | "guided" | "history";
  topRightSlot?: ReactNode;
  bottomSlot?: ReactNode;
  subDrawerHeader?: ReactNode;
  statusIndicator?: DrawerHeaderProps["status"];
};
export const RegistrationDrawerHeader: FC<RegistrationDrawerHeaderProps> = ({
  registration,
  activeTab,
  topRightSlot,
  bottomSlot,
  subDrawerHeader,
  statusIndicator,
}) => {
  const variant = useRegistrationVariant();
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const closeDrawer = () => navigate(paths.registrationsGrid({ variant }));

  return (
    <>
      <Drawer.Header
        status={statusIndicator}
        onClose={closeDrawer}
        title={registration.name}
        renderIcon={({ ...props }) => {
          const Icon = variant === "Controller" ? RegistrationControllerIcon2 : RegistrationProcessorIcon2;
          return <Icon {...props} />;
        }}
        topRightSlot={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {topRightSlot}
            <RegistrationActionMenu
              registrationId={registration.id}
              onDeleted={closeDrawer}
              onCopied={newRegistrationId => {
                const id = newRegistrationId;
                if (activeTab === "standard") navigate(paths.registrationsInline({ id, variant }));
                else if (activeTab === "guided") navigate(paths.registrationsMultiStep({ id, variant }));
                else if (activeTab === "history") navigate(paths.registrationsHistory({ id, variant }));
                else logUnhandled(activeTab, "activeTab");
              }}
              ariaLabelKey="GridDrawer.Menu.ButtonLabel"
            />
          </Box>
        }
        bottomSlot={
          <>
            <Stack
              direction="row"
              spacing={1}
              sx={{ px: Drawer.PADDING_X, alignItems: "flex-end", justifyContent: "space-between" }}
            >
              <Drawer.TabButtons>
                <Drawer.TabButton
                  active={activeTab === "standard"}
                  to={paths.registrationsInline({ id: registration.id, variant })}
                >
                  {t("Dynamic.Drawer.OverviewButton")}
                </Drawer.TabButton>
                <Drawer.TabButton
                  active={activeTab === "guided"}
                  to={paths.registrationsMultiStep({ id: registration.id, variant })}
                  onClick={() => trackEvent.clickRegistrationGuidedTab()}
                >
                  {t("RegistrationGrid.Drawer.GuidedViewButton")}
                </Drawer.TabButton>
                <Drawer.TabButton
                  active={activeTab === "history"}
                  to={paths.registrationsHistory({ id: registration.id, variant })}
                  onClick={() => trackEvent.clickRegistrationHistoryTab()}
                  onHover={() => prefetchRegistrationHistory(registration.id, variant)}
                >
                  {t("Dynamic.Drawer.HistoryButton")}
                </Drawer.TabButton>
              </Drawer.TabButtons>

              {activeTab === "guided" && bottomSlot}
            </Stack>
            {activeTab === "standard" && bottomSlot}
          </>
        }
      />
      {subDrawerHeader}
    </>
  );
};

export const RegistrationActionMenu = ({
  registrationId,
  onDeleted,
  onCopied,
  ariaLabelKey,
}: {
  registrationId: string;
  onDeleted?: () => void;
  onCopied: (newRegistrationId: string) => void;
  ariaLabelKey?: TranslationKey;
}) => {
  const variant = useRegistrationVariant();
  const { hasPermission } = usePermission();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const registrationQuery = useGetRegistrationById(registrationId, variant, { enabled: isMenuOpen });
  const { t } = useTranslation();
  const deleteRegistrationMutation = useDeleteRegistration();
  const [openDialog, setOpenDialog] = useState<"edit" | "copy" | null>(null);

  const closeDialogAndMenu = () => {
    setOpenDialog(null);
    setIsMenuOpen(false);
  };

  const hasPermissionToUpdateMetaData = useHasPermissionToUpdateMetaData();

  return (
    <>
      {openDialog === "edit" && registrationQuery.data && (
        <RegistrationEditDialog onClose={closeDialogAndMenu} registration={registrationQuery.data} />
      )}
      {openDialog === "copy" && registrationQuery.data && (
        <CopyRegistrationDialog
          registration={registrationQuery.data}
          onClose={closeDialogAndMenu}
          onCopied={newRegistrationId => {
            closeDialogAndMenu();
            onCopied(newRegistrationId);
          }}
        />
      )}
      <ActionMenu
        isOpen={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        onTriggerHover={() => prefetchRegistrationById(registrationId, variant)}
        ariaLabelKey={ariaLabelKey}
      >
        <ActionMenuItemWithPermission
          missingPermission={registrationQuery.data && !hasPermissionToUpdateMetaData(registrationQuery.data)}
          disabled={registrationQuery.isLoading}
          onClick={() => setOpenDialog("edit")}
        >
          <EditIcon />
          {t("RegistrationDrawer.Edit.Label")}
        </ActionMenuItemWithPermission>

        <ActionMenuItemWithPermission
          missingPermission={!hasPermission("nova-forms-api:registration:create")}
          disabled={registrationQuery.isLoading}
          onClick={() => setOpenDialog("copy")}
          onHover={() => void prefetch(myTenantsHierarchyWithCreateRegistrationPermissionQueryOptions())}
        >
          <FilesIcon />
          {t("RegistrationDrawer.Copy.Label")}
        </ActionMenuItemWithPermission>

        <ActionMenuDeleteMenuItem
          missingPermission={
            registrationQuery.data &&
            !hasPermission("nova-forms-api:registration:delete", registrationQuery.data.permissions)
          }
          disabled={registrationQuery.isLoading}
          confirmDeleteDialog={{
            header: t("RegistrationDrawer.Delete.ConfirmHeader"),
            message: (
              <Translate
                translationKey="RegistrationDrawer.Delete.ConfirmMessage"
                values={{
                  name: (
                    <Box component="span" key={registrationQuery.data?.name} sx={{ fontWeight: "bold" }}>
                      {registrationQuery.data?.name}
                    </Box>
                  ),
                }}
              />
            ),
            delete: t("RegistrationDrawer.Delete.ConfirmDelete"),
          }}
          onClose={() => setIsMenuOpen(false)}
          onDelete={() => {
            if (!registrationQuery.data) throw new Error("Registration should always be defined here.");

            return deleteRegistrationMutation.mutateAsync(registrationQuery.data.id, {
              onSuccess: () => {
                onDeleted?.();
                setIsMenuOpen(false);
              },
            });
          }}
        />
      </ActionMenu>
    </>
  );
};
